import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import OpenInNewIcon from '@mui/icons-material/EditOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import DoublonIcon from '@mui/icons-material/FileCopyOutlined';
import ErreurIcon from '@mui/icons-material/ErrorOutline';
import DupliquerIcon from '@mui/icons-material/ContentCopy';
import PublierIcon from '@mui/icons-material/GroupsOutlined';
import RetirerIcon from '@mui/icons-material/GroupRemoveOutlined';
import { analyserSeance, normaliserAllures } from './seanceEngine.ts';
import { useVma } from '../Allures/useVma.ts';
import { doublonsParSeance, nomDoublon } from './doublons.ts';
import { chargerClub, type Club } from '../Auth/moi.ts';
import { useEtatSession } from '../useEtatSession.ts';

import { ZoneChip } from '../Allures/couleursZones.tsx';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { TYPES_SEANCE, typeAutomatique, typeEffectif, type TypeSeance } from './classification.ts';
import TypeSeanceChip from './TypeSeanceChip.tsx';
import { formaterDuree } from './formatDuree.ts';
import {
  dupliquerSeance,
  duSocle,
  enLectureSeule,
  enregistrerSeance,
  listerSeances,
  publierSeance,
  retirerDuSocle,
  supprimerSeance,
  avertissementCalendriers,
  type Seance,
} from './seancesApi.ts';
import CategorieChip from './CategorieChip.tsx';
import {
  aUnType,
  CATEGORIES,
  categorieDe,
  descriptionModele,
  libelleUsage,
  partiesModele,
  syntheseModele,
  usageDe,
  usagesDuModele,
  USAGES_MODELE,
  type Categorie,
  type ModelesParDefaut,
  type InfoUsage,
} from './composition.ts';

// typeAffiche : type enregistre, ou calcule depuis la notation pour les
// seances enregistrees avant la classification (corps de seance seulement).
// notationAffichee : notation, ou composition pour un modele.
// modeleAffiche : corps de seance seulement, modele force ou MODELE_AUTO.
type Ligne = Seance & { id: string; typeAffiche?: string | null; notationAffichee?: string; modeleAffiche?: string | null };

// Valeur "choix automatique du modele" dans l'edition de la colonne Modèle.
const MODELE_AUTO = 'AUTO';

// Valeur "retour au type automatique" dans l'edition de la colonne Type.
const TYPE_AUTO = 'AUTO';

// Filtres de la liste : tranches de duree (minutes) et de charge (UA).
const TOUTES = '__toutes__';
// Filtre de portee : seances personnelles ou bibliotheque du club.
const PORTEES = [
  { cle: 'PERSO', libelle: 'Mes séances' },
  { cle: 'SOCLE', libelle: 'Bibliothèque du club' },
];
type Tranche = { cle: string; libelle: string; min: number; max: number };
const TRANCHES_DUREE: Tranche[] = [
  { cle: 'd30', libelle: 'Moins de 30 min', min: 0, max: 30 },
  { cle: 'd45', libelle: '30 à 45 min', min: 30, max: 45 },
  { cle: 'd60', libelle: '45 min à 1h', min: 45, max: 60 },
  { cle: 'd+', libelle: "Plus d'1h", min: 60, max: Infinity },
];
const TRANCHES_UA: Tranche[] = [
  { cle: 'u150', libelle: 'Moins de 150 UA', min: 0, max: 150 },
  { cle: 'u250', libelle: '150 à 250 UA', min: 150, max: 250 },
  { cle: 'u300', libelle: '250 à 300 UA', min: 250, max: 300 },
  { cle: 'u+', libelle: 'Plus de 300 UA', min: 300, max: Infinity },
];

function dansTranche(valeur: number | null | undefined, cle: string, tranches: Tranche[]): boolean {
  if (cle === TOUTES) return true;
  const t = tranches.find((x) => x.cle === cle);
  return !!t && valeur !== null && valeur !== undefined && valeur >= t.min && valeur < t.max;
}

function formaterDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Liste triable des seances enregistrees : un clic (ou le crayon) ouvre la
// seance dans l'editeur, la corbeille la supprime.
export default function SeancesEnregistrees({
  rafraichissement,
  seanceOuverte,
  onOuvrir,
  onSuppression,
  onRenommage,
  onSeancesChargees,
  modelesParDefaut,
}: {
  rafraichissement: number;
  /** Id de la seance en cours d'edition (mise en evidence). */
  seanceOuverte?: string;
  onOuvrir: (seance: Seance) => void;
  onSuppression?: (id: string) => void;
  /** Appele apres un renommage dans le tableau (pour mettre a jour l'editeur). */
  onRenommage?: (id: string, nom: string) => void;
  /** Liste chargee (ou modifiee) : utile a l'editeur pour composer les modeles. */
  onSeancesChargees?: (seances: Seance[]) => void;
  /** Modeles par defaut de l'utilisateur (signales dans la liste). */
  modelesParDefaut?: ModelesParDefaut;
}) {
  const [seances, setSeances] = React.useState<Ligne[]>([]);
  // Filtres conserves dans la session (retour depuis l'editeur, rechargement).
  const [recherche, setRecherche] = useEtatSession('seances.filtre.recherche', '');
  const [allure, setAllure] = useEtatSession('seances.filtre.allure', TOUTES);
  const [duree, setDuree] = useEtatSession('seances.filtre.duree', TOUTES);
  const [charge, setCharge] = useEtatSession('seances.filtre.charge', TOUTES);
  const [type, setType] = useEtatSession('seances.filtre.type', TOUTES);
  // Categories retenues (filtre multiple) ; vide = toutes.
  const [categoriesFiltre, setCategoriesFiltre] = useEtatSession<string[]>('seances.filtre.categories', []);
  const [portee, setPortee] = useEtatSession('seances.filtre.portee', TOUTES);
  // Club du coach (bibliotheque du club) : null sans club.
  const [groupe, setGroupe] = React.useState<Club | null>(null);
  React.useEffect(() => {
    chargerClub().then(setGroupe).catch(() => setGroupe(null));
  }, [rafraichissement]);
  // Bibliothecaire : publie dans la bibliotheque du club ses seances personnelles cochees.
  const [selection, setSelection] = React.useState<string[]>([]);
  const { table } = useAllureTable();
  const [vma] = useVma();
  // Seances identiques entre elles (meme suite d'etapes), cf doublons.ts.
  const doublons = React.useMemo(() => doublonsParSeance(seances, vma, table), [seances, vma, table]);
  const [doublonsSeulement, setDoublonsSeulement] = useEtatSession('seances.filtre.doublons', false);
  // Seances dont la notation n'est plus valide (ex : saisie erronee, ou zone
  // d'allure inconnue apres un changement de parametres) : id -> message.
  const erreurs = React.useMemo(() => {
    const parId = new Map<string, string>();
    seances.forEach((s) => {
      if (!s.id || categorieDe(s) === 'MODELE') return;
      const erreur = analyserSeance(s.notation, vma > 0 ? vma : 16.5, table).synthese.erreur;
      if (erreur) parId.set(s.id, erreur);
    });
    return parId;
  }, [seances, vma, table]);
  const [erreursSeulement, setErreursSeulement] = useEtatSession('seances.filtre.erreurs', false);

  React.useEffect(() => {
    onSeancesChargees?.(seances);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seances]);

  // Un modele affiche sa composition et la synthese de ses parties
  // (echauffement + recuperation) ; seul un corps de seance a un type.
  const lignes = React.useMemo<Ligne[]>(
    () =>
      seances.map((s) => {
        const cat = categorieDe(s);
        if (cat === 'MODELE') {
          const parties = partiesModele(s, seances);
          return { ...s, ...syntheseModele(parties), typeAffiche: null, notationAffichee: descriptionModele(parties) };
        }
        return {
          ...s,
          typeAffiche: aUnType(cat) ? typeEffectif(s, table) : null,
          notationAffichee: normaliserAllures(s.notation),
          modeleAffiche: cat === 'CORPS' ? s.modeleSeance ?? MODELE_AUTO : null,
        };
      }),
    [seances, table],
  );

  // Allures (zones principales) presentes parmi les seances.
  const allures = React.useMemo(
    () => Array.from(new Set(seances.map((s) => s.zonePrincipale).filter((z): z is string => !!z))).sort(),
    [seances],
  );

  const seancesFiltrees = React.useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return lignes.filter(
      (s) =>
        (!q || `${s.nom} ${s.notationAffichee ?? ''} ${s.notes ?? ''}`.toLowerCase().includes(q))
        && (categoriesFiltre.length === 0 || categoriesFiltre.includes(categorieDe(s)))
        && (allure === TOUTES || s.zonePrincipale === allure)
        && dansTranche(s.dureeTotaleS === null || s.dureeTotaleS === undefined ? null : s.dureeTotaleS / 60, duree, TRANCHES_DUREE)
        && dansTranche(s.chargeUA, charge, TRANCHES_UA)
        && (type === TOUTES || s.typeAffiche === type)
        && (portee === TOUTES || (s.portee ?? 'PERSO') === portee)
        && (!doublonsSeulement || doublons.has(s.id))
        && (!erreursSeulement || erreurs.has(s.id)),
    );
  }, [lignes, recherche, allure, duree, charge, type, portee, categoriesFiltre, doublonsSeulement, doublons, erreursSeulement, erreurs]);

  const filtresActifs = !!recherche.trim() || portee !== TOUTES || doublonsSeulement || erreursSeulement || categoriesFiltre.length > 0 || allure !== TOUTES || duree !== TOUTES || charge !== TOUTES || type !== TOUTES;
  const reinitialiserFiltres = () => {
    setRecherche('');
    setPortee(TOUTES);
    setCategoriesFiltre([]);
    setDoublonsSeulement(false);
    setErreursSeulement(false);
    setAllure(TOUTES);
    setDuree(TOUTES);
    setCharge(TOUTES);
    setType(TOUTES);
  };
  const [chargement, setChargement] = React.useState(true);
  const [erreur, setErreur] = React.useState<string | null>(null);

  const charger = React.useCallback(() => {
    setChargement(true);
    listerSeances()
      .then((liste: Seance[]) => {
        setSeances(liste.filter((s: Seance) => s.id).map((s: Seance) => ({ ...s, id: s.id as string })));
        setErreur(null);
      })
      .catch((e: Error) => setErreur('Impossible de charger tes séances (' + e.message + ')'))
      .finally(() => setChargement(false));
  }, []);

  React.useEffect(() => {
    charger();
  }, [charger, rafraichissement]);

  // Modification depuis le tableau (nom ou type) : la seance est
  // reenregistree telle quelle avec la valeur modifiee. Une erreur annule.
  const mettreAJour = async (nouvelle: Ligne, ancienne: Ligne): Promise<Ligne> => {
    const nom = (nouvelle.nom ?? '').trim();
    const typeChange = nouvelle.typeAffiche !== ancienne.typeAffiche;
    const modeleChange = nouvelle.modeleAffiche !== ancienne.modeleAffiche;
    if (nom === ancienne.nom && !typeChange && !modeleChange) return ancienne;
    if (!nom) throw new Error('le nom ne peut pas être vide');
    if (nom.length > 100) throw new Error('100 caractères maximum');
    let typeSeance = ancienne.typeSeance ?? ancienne.typeAffiche ?? null;
    let typeForce = !!ancienne.typeForce;
    if (typeChange) {
      if (nouvelle.typeAffiche === TYPE_AUTO) {
        typeSeance = typeAutomatique(ancienne.notation, ancienne.vma, table);
        typeForce = false;
      } else {
        typeSeance = nouvelle.typeAffiche ?? null;
        typeForce = true;
      }
    }
    const modeleSeance = modeleChange
      ? (nouvelle.modeleAffiche === MODELE_AUTO ? null : nouvelle.modeleAffiche ?? null)
      : ancienne.modeleSeance ?? null;
    const { typeAffiche, notationAffichee, modeleAffiche, ...seance } = seances.find((s) => s.id === ancienne.id) ?? ancienne;
    const enregistree = await enregistrerSeance({ ...seance, nom, typeSeance, typeForce, modeleSeance });
    const ligne: Ligne = {
      ...ancienne,
      ...enregistree,
      id: ancienne.id,
      typeAffiche: enregistree.typeSeance,
      modeleAffiche: categorieDe(enregistree) === 'CORPS' ? enregistree.modeleSeance ?? MODELE_AUTO : null,
    };
    setSeances((actuelles) => actuelles.map((s) => (s.id === ligne.id ? { ...enregistree, id: ligne.id } : s)));
    setErreur(null);
    if (nom !== ancienne.nom) onRenommage?.(ligne.id, ligne.nom);
    return ligne;
  };

  // Copie personnelle d'une seance du socle, ouverte dans l'editeur.
  const dupliquer = (seance: Ligne) => {
    dupliquerSeance(seance.id)
      .then((copie: Seance) => {
        charger();
        onOuvrir(copie);
      })
      .catch((e: Error) => setErreur('Copie impossible (' + e.message + ')'));
  };

  // Publication dans le socle (responsable) : une seance identique deja
  // dans le socle est signalee avant de publier.
  const publier = async (aPublier: Ligne[]) => {
    if (aPublier.length === 0) return;
    const enDouble = aPublier
      .map((s) => ({ s, socle: (doublons.get(s.id) ?? []).filter(duSocle) }))
      .filter((x) => x.socle.length > 0);
    const avertissement = enDouble.length > 0
      ? '\n\nDéjà dans la bibliothèque du club :\n' + enDouble.map((x) => `« ${x.s.nom} » = ${x.socle.map(nomDoublon).join(', ')}`).join('\n')
      : '';
    const question = aPublier.length === 1
      ? `Publier « ${aPublier[0].nom} » dans la bibliothèque du club ? Tous les coachs du club la verront ; seuls les bibliothécaires pourront la modifier.`
      : `Publier ${aPublier.length} séances dans la bibliothèque du club ? Tous les coachs du club les verront ; seuls les bibliothécaires pourront les modifier.`;
    if (!window.confirm(question + avertissement)) return;
    try {
      for (const s of aPublier) await publierSeance(s.id);
      setSelection([]);
      setErreur(null);
    } catch (e) {
      setErreur('Publication impossible (' + (e as Error).message + ')');
    }
    charger();
  };

  // Ne plus partager une seance du socle (responsable) : elle redevient personnelle.
  const retirer = async (seance: Ligne) => {
    const avertissement = await avertissementCalendriers(seance.id);
    if (!window.confirm(`Retirer « ${seance.nom} » de la bibliothèque du club ? Elle ne sera plus partagée : elle redevient une de tes séances personnelles. Les séances déjà posées dans les calendriers sont conservées.` + avertissement)) return;
    retirerDuSocle(seance.id)
      .then(() => {
        setErreur(null);
        charger();
      })
      .catch((e: Error) => setErreur('Retrait de la bibliothèque impossible (' + e.message + ')'));
  };

  const supprimer = async (seance: Ligne) => {
    const avertissement = await avertissementCalendriers(seance.id);
    const question = duSocle(seance)
      ? `Supprimer « ${seance.nom} » de la bibliothèque du club ? Elle disparaîtra pour tous les coachs du club (les séances déjà posées dans les calendriers sont conservées).`
      : `Supprimer la séance « ${seance.nom} » ?`;
    if (!window.confirm(question + avertissement)) return;
    supprimerSeance(seance.id)
      .then(() => {
        charger();
        onSuppression?.(seance.id);
      })
      .catch((e: Error) => setErreur('Suppression impossible (' + e.message + ')'));
  };

  const columns: GridColDef<Ligne>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={enLectureSeule(params.row) ? 'Ouvrir (lecture seule)' : "Ouvrir dans l'éditeur"}>
            <IconButton size="small" aria-label="Ouvrir" onClick={(e) => { e.stopPropagation(); onOuvrir(params.row); }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {duSocle(params.row) && (
            <Tooltip title="Dupliquer dans mes séances, pour l'adapter">
              <IconButton size="small" aria-label="Dupliquer" onClick={(e) => { e.stopPropagation(); dupliquer(params.row); }}>
                <DupliquerIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {duSocle(params.row) && !enLectureSeule(params.row) && (
            <Tooltip title="Retirer de la bibliothèque du club (ne plus partager)">
              <IconButton size="small" aria-label="Retirer de la bibliothèque" onClick={(e) => { e.stopPropagation(); retirer(params.row); }}>
                <RetirerIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!duSocle(params.row) && groupe?.bibliothecaire && (
            <Tooltip title="Publier dans la bibliothèque du club">
              <IconButton size="small" aria-label="Publier" onClick={(e) => { e.stopPropagation(); publier([params.row]); }}>
                <PublierIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!enLectureSeule(params.row) && (
            <Tooltip title={duSocle(params.row) ? 'Supprimer du socle commun' : 'Supprimer'}>
              <IconButton size="small" aria-label="Supprimer" onClick={(e) => { e.stopPropagation(); supprimer(params.row); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'nom',
      headerName: 'Séance',
      flex: 1,
      minWidth: 180,
      // Renommage rapide : double-clic sur la cellule, Entree pour valider,
      // Echap pour annuler (enregistre immediatement, cf processRowUpdate).
      editable: true,
      renderCell: (params) => {
        const identiques = doublons.get(params.row.id) ?? [];
        const erreur = erreurs.get(params.row.id);
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {erreur && (
              <Tooltip title={`Séance en erreur : ${erreur}`}>
                <ErreurIcon fontSize="small" color="error" />
              </Tooltip>
            )}
            {identiques.length > 0 && (
              <Tooltip title={`Doublon : identique à ${identiques.map(nomDoublon).join(', ')}`}>
                <DoublonIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
            <Tooltip title={enLectureSeule(params.row) ? '' : 'Double-clic pour renommer'} enterDelay={600}>
              <span>{params.value}</span>
            </Tooltip>
            {duSocle(params.row) && (
              <Tooltip title="Bibliothèque du club">
                <Chip size="small" color="info" variant="outlined" label="Club" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
            {params.row.idOrigine && (
              <Tooltip title={`Copie de « ${seances.find((s) => s.id === params.row.idOrigine)?.nom ?? 'une séance de la bibliothèque'} »`}>
                <Chip size="small" variant="outlined" label="Copie" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'categorie',
      headerName: 'Catégorie',
      width: 150,
      valueGetter: (_value, row) => categorieDe(row),
      renderCell: (params) => (
        <CategorieChip
          code={params.value}
          parDefaut={modelesParDefaut ? usagesDuModele(params.row.id, modelesParDefaut).map(libelleUsage) : []}
        />
      ),
    },
    {
      field: 'notationAffichee',
      headerName: 'Notation',
      flex: 1.3,
      minWidth: 220,
      renderCell: (params) =>
        categorieDe(params.row) === 'MODELE'
          ? <i style={{ fontSize: '0.85rem' }}>{params.value}</i>
          : <code style={{ fontSize: '0.8rem' }}>{params.value}</code>,
    },
    {
      field: 'typeAffiche',
      headerName: 'Type',
      width: 170,
      // Correction rapide du type : double-clic, puis choix dans la liste
      // ("Automatique" revient au type deduit de la notation).
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: TYPE_AUTO, label: 'Automatique' },
        ...TYPES_SEANCE.map((t: TypeSeance) => ({ value: t.code, label: t.libelle })),
      ],
      renderCell: (params) => <TypeSeanceChip code={params.value} force={params.row.typeForce} />,
    },
    {
      field: 'modeleAffiche',
      headerName: 'Modèle',
      width: 170,
      // Corps de seance : modele applique dans le calendrier. Double-clic
      // pour le forcer ("Automatique" = selon le type de seance).
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: MODELE_AUTO, label: 'Automatique' },
        ...USAGES_MODELE.map((u: InfoUsage) => ({ value: u.code, label: u.libelle })),
        { value: 'AUCUN', label: 'Aucun' },
      ],
      renderCell: (params) => {
        if (!params.value) return null;
        const auto = params.value === MODELE_AUTO;
        const usage = usageDe(auto ? null : params.value, params.row.typeAffiche);
        return (
          <Tooltip title={auto ? 'Choisi selon le type de séance — double-clic pour le forcer' : 'Choisi à la main — double-clic pour modifier'}>
            <span style={{ fontStyle: auto ? 'italic' : 'normal' }}>
              {libelleUsage(usage)}{auto ? ' (auto)' : ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'zonePrincipale',
      headerName: 'Allure',
      width: 170,
      renderCell: (params) => <ZoneChip nom={params.value} />,
    },
    {
      field: 'dureeTotaleS',
      headerName: 'Durée',
      type: 'number',
      width: 100,
      // Affichage en heures/minutes (secondes ignorees) ; le tri reste
      // numerique sur la duree en secondes.
      valueFormatter: (value: number | null) => (value === null || value === undefined ? '' : formaterDuree(value)),
    },
    {
      field: 'distanceTotaleM',
      headerName: 'Distance',
      type: 'number',
      width: 100,
      valueFormatter: (value: number | null) =>
        value === null || value === undefined ? '' : `${(value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} km`,
    },
    {
      field: 'chargeUA',
      headerName: 'Charge (UA)',
      type: 'number',
      width: 110,
      // UA entiere (les seances enregistrees avant l'arrondi peuvent avoir une decimale)
      valueGetter: (value: number | null) => (value === null || value === undefined ? null : Math.round(value)),
    },
    { field: 'rpe', headerName: 'RPE', type: 'number', width: 80 },
    {
      field: 'vma',
      headerName: 'VMA',
      type: 'number',
      width: 90,
      valueFormatter: (value: number | null) => (value === null || value === undefined ? '' : `${value.toLocaleString('fr-FR')}`),
    },
    {
      field: 'dateModification',
      headerName: 'Modifiée le',
      type: 'dateTime',
      width: 120,
      valueGetter: (value: string | null) => (value ? new Date(value) : null),
      valueFormatter: (value: Date | null) => (value ? formaterDate(value.toISOString()) : ''),
    },
  ];

  return (
    <Box>
      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {seances.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Rechercher (nom, notation, notes)…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260, flex: '1 1 260px', maxWidth: 380 }}
          />
          {(groupe || seances.some(duSocle)) && (
            <TextField select size="small" label="Portée" value={portee} onChange={(e) => setPortee(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value={TOUTES}>Toutes</MenuItem>
              {PORTEES.map((p) => <MenuItem key={p.cle} value={p.cle}>{p.libelle}</MenuItem>)}
            </TextField>
          )}
          <TextField
            select
            size="small"
            label="Catégories"
            value={categoriesFiltre}
            onChange={(e) => {
              const v = e.target.value as unknown as string | string[];
              setCategoriesFiltre(typeof v === 'string' ? v.split(',').filter(Boolean) : v);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selection) => {
                const codes = selection as string[];
                if (codes.length === 0) return 'Toutes';
                return CATEGORIES.filter((c: Categorie) => codes.includes(c.code)).map((c: Categorie) => c.libelle).join(', ');
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200, maxWidth: 320 }}
          >
            {CATEGORIES.map((c: Categorie) => (
              <MenuItem key={c.code} value={c.code} dense>
                <Checkbox size="small" checked={categoriesFiltre.includes(c.code)} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText primary={c.libelle} />
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 190 }}>
            <MenuItem value={TOUTES}>Tous</MenuItem>
            {TYPES_SEANCE.map((t: TypeSeance) => <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Allure" value={allure} onChange={(e) => setAllure(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value={TOUTES}>Toutes</MenuItem>
            {allures.map((nom) => (
              <MenuItem key={nom} value={nom}>
                <ZoneChip nom={nom} />
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Durée" value={duree} onChange={(e) => setDuree(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value={TOUTES}>Toutes</MenuItem>
            {TRANCHES_DUREE.map((t) => <MenuItem key={t.cle} value={t.cle}>{t.libelle}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Charge" value={charge} onChange={(e) => setCharge(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value={TOUTES}>Toutes</MenuItem>
            {TRANCHES_UA.map((t) => <MenuItem key={t.cle} value={t.cle}>{t.libelle}</MenuItem>)}
          </TextField>
          {erreurs.size > 0 && (
            <Tooltip title="Séances dont la notation n'est pas valide : ouvre-les pour les corriger">
              <Chip
                icon={<ErreurIcon />}
                color="error"
                variant={erreursSeulement ? 'filled' : 'outlined'}
                label={`En erreur (${erreurs.size})`}
                onClick={() => setErreursSeulement((v) => !v)}
              />
            </Tooltip>
          )}
          {doublons.size > 0 && (
            <Tooltip title="Séances identiques (même suite d'efforts et de récupérations, quelle que soit l'écriture)">
              <Chip
                icon={<DoublonIcon />}
                color="warning"
                variant={doublonsSeulement ? 'filled' : 'outlined'}
                label={`Doublons (${doublons.size})`}
                onClick={() => setDoublonsSeulement((v) => !v)}
              />
            </Tooltip>
          )}
          {groupe?.bibliothecaire && selection.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublierIcon />}
              onClick={() => publier(lignes.filter((s) => selection.includes(s.id) && !duSocle(s)))}
            >
              Publier la sélection dans la bibliothèque ({selection.length})
            </Button>
          )}
          {filtresActifs && (
            <>
              <Typography variant="body2" color="text.secondary">
                {seancesFiltrees.length} séance(s) sur {seances.length}
              </Typography>
              <Button size="small" onClick={reinitialiserFiltres}>Réinitialiser</Button>
            </>
          )}
        </Box>
      )}
      {!chargement && !erreur && seances.length === 0 ? (
        <Alert severity="info">Aucune séance enregistrée pour l'instant : construis-en une ci-dessus et enregistre-la.</Alert>
      ) : (
        <DataGrid
          rows={seancesFiltrees}
          columns={columns}
          loading={chargement}
          autoHeight
          disableRowSelectionOnClick
          // Bibliothecaire du club : coche ses seances a publier dans la bibliotheque.
          checkboxSelection={!!groupe?.bibliothecaire}
          isRowSelectable={(params) => !duSocle(params.row)}
          rowSelectionModel={selection}
          onRowSelectionModelChange={(modele) => setSelection(modele.map(String))}
          // Clic sur une cellule : ouvre la seance dans l'editeur, sauf sur le
          // nom (reserve au renommage par double-clic) et les actions.
          onCellClick={(params) => {
            if (params.field !== 'nom' && params.field !== 'typeAffiche' && params.field !== 'modeleAffiche' && params.field !== 'actions' && params.field !== '__check__') onOuvrir(params.row);
          }}
          // Le type ne concerne que les seances posees dans le calendrier.
          isCellEditable={(params) =>
            !enLectureSeule(params.row)
            && (params.field !== 'typeAffiche' || aUnType(categorieDe(params.row)))
            && (params.field !== 'modeleAffiche' || categorieDe(params.row) === 'CORPS')}
          processRowUpdate={mettreAJour}
          onProcessRowUpdateError={(e: Error) => setErreur('Modification impossible (' + e.message + ')')}
          getRowClassName={(params) => (params.id === seanceOuverte ? 'seance-ouverte' : '')}
          initialState={{
            sorting: { sortModel: [{ field: 'dateModification', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{
            backgroundColor: 'background.paper',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .seance-ouverte': { backgroundColor: 'action.selected' },
          }}
        />
      )}
    </Box>
  );
}
